import { useState, useEffect, useRef } from 'react';
import { 
  GraduationCap,
  Shield,
  LogOut,
  ChevronRight,
  CalendarDays,
  Users,
  BookOpen,
  CircleDollarSign,
  Settings2,
  Layout,
  HardDrive,
  MessageCircle,
  PenTool,
  Layers,
  Brain,
  Bot,
  Sparkles,
} from 'lucide-react';
import { getTutees, verifyPin } from '../../config/tutees';
import { ADMIN_CONFIG } from '../../config/admin';
import { Tutee } from '../../types/tuition';
import PinProtection from './PinProtection';
import AdminPinProtection from './AdminPinProtection';
import TuteeDashboard from './TuteeDashboard';
import BookingRequestsAdmin from './BookingRequestsAdmin';
import Skeleton from '../../components/ui/Skeleton';
import TuteeEditor from './admin/TuteeEditor';
import TuteeCreator from './admin/TuteeCreator';
import TuteeDeleteModal from './admin/TuteeDeleteModal';
import ComponentManager from './admin/ComponentManager';
import FeedbackAdmin from './admin/FeedbackAdmin';
import EarningsAdmin from './admin/EarningsAdmin';
import SpellingQuizConfig from './admin/SpellingQuizConfig';
import HanziHeroConfig from './admin/HanziHeroConfig';
import FlashcardsConfig from './admin/FlashcardsConfig';
import GPTChatAdmin from './admin/GPTChatAdmin';
import GlobalFileManager from './admin/GlobalFileManager';
import NotificationAdmin from './admin/NotificationAdmin';
import MessagingAdmin from './admin/MessagingAdmin';
import AdminQuickLinks from './admin/AdminQuickLinks';
import LearningPointsAdmin from './admin/LearningPointsAdmin';
import ProgressReportAdmin from './admin/ProgressReportAdmin';
import LandingPageSettings from './admin/LandingPageSettings';
import StudentManagement from './admin/StudentManagement';
import StickyAdminGroupBar from './admin/StickyAdminGroupBar';
import AdminCollapsibleSection from './admin/AdminCollapsibleSection';
import AdminTuitionCalendar from './admin/AdminTuitionCalendar';
import { notificationService } from '../../services/notificationService';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { getLandingPagePreference } from '../../utils/landingPagePreference';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTuteeIcon } from '../../utils/tuteeIcons';
import { getColorClasses, getPrimaryHex } from '../../utils/colorUtils';
import TuitionMascot from './TuitionMascot';

const ADMIN_TAB_STORAGE_KEY = 'tuition-admin-tab';

const ADMIN_TAB_IDS = ['schedule', 'students', 'learning', 'business', 'system'] as const;
type AdminTabId = (typeof ADMIN_TAB_IDS)[number];

function readStoredAdminTab(): AdminTabId {
  try {
    const raw = localStorage.getItem(ADMIN_TAB_STORAGE_KEY);
    if (raw && ADMIN_TAB_IDS.includes(raw as AdminTabId)) return raw as AdminTabId;
  } catch {
    /* ignore */
  }
  return 'schedule';
}

const Tuition = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTutee, setSelectedTutee] = useState<Tutee | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingTutee, setPendingTutee] = useState<Tutee | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPinModal, setShowAdminPinModal] = useState(false);
  const [tutees, setTutees] = useState<Tutee[]>([]);
  const [loadingTutees, setLoadingTutees] = useState(true);
  const [selectedEarningsTuteeId, setSelectedEarningsTuteeId] = useState<string | null>(null);
  const [selectedStudentsTuteeId, setSelectedStudentsTuteeId] = useState<string>('');
  const [selectedLearningTuteeId, setSelectedLearningTuteeId] = useState<string>('');
  const [isAdminSubscribed, setIsAdminSubscribed] = useState(false);
  const [isCheckingAdminSub, setIsCheckingAdminSub] = useState(true);
  const [isTogglingAdminSub, setIsTogglingAdminSub] = useState(false);
  const [isAdminTestingNotification, setIsAdminTestingNotification] = useState(false);
  const [adminTab, setAdminTab] = useState<AdminTabId>(readStoredAdminTab);
  const [mobileActionsTuteeId, setMobileActionsTuteeId] = useState<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  useEffect(() => {
    if (!mobileActionsTuteeId) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(`[data-tutee-actions="${mobileActionsTuteeId}"]`)) return;
      setMobileActionsTuteeId(null);
    };

    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [mobileActionsTuteeId]);

  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_TAB_STORAGE_KEY, adminTab);
    } catch {
      /* ignore */
    }
  }, [adminTab]);

  // Auto-redirect based on landing page preference (bypass with ?stay=true)
  useEffect(() => {
    const shouldStay = searchParams.get('stay') === 'true';
    if (shouldStay) return; // Don't redirect if ?stay=true
    
    const preference = getLandingPagePreference();
    // Only redirect if preference is explicitly set AND it's not /tuition
    if (preference !== null && preference !== '/tuition') {
      if (preference.endsWith('.html')) {
        window.location.assign(preference);
      } else {
        navigate(preference, { replace: true });
      }
    }
  }, [navigate, searchParams]);

  // Check admin subscription status
  useEffect(() => {
    const checkAdminSub = async () => {
      if (isAdmin && notificationService.isSupported()) {
        const subscribed = await notificationService.isSubscribed('admin');
        setIsAdminSubscribed(subscribed);
      }
      setIsCheckingAdminSub(false);
    };
    checkAdminSub();
  }, [isAdmin]);

  const handleAdminNotificationToggle = async () => {
    try {
      setIsTogglingAdminSub(true);
      if (isAdminSubscribed) {
        await notificationService.unsubscribeUser('admin');
        setIsAdminSubscribed(false);
      } else {
        await notificationService.subscribeUser('admin');
        setIsAdminSubscribed(true);
        
        // Immediate "Welcome" notification for the Admin
        setTimeout(() => {
          notificationService.notify({
            type: 'admin_welcome',
            tuteeId: 'admin',
            title: 'Admin Notifications Enabled! 🛡️',
            message: 'You will now receive alerts for new requests, feedback, and uploads.',
            url: '/tuition'
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to toggle admin notifications:', error);
      alert('Failed to update notification settings.');
    } finally {
      setIsTogglingAdminSub(false);
    }
  };

  const handleAdminTestNotification = async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isAdminSubscribed || isAdminTestingNotification) return;

    setIsAdminTestingNotification(true);
    const btn = e.currentTarget as HTMLButtonElement;

    try {
      await notificationService.notify({
        type: 'admin_test',
        tuteeId: 'admin',
        title: 'Admin Test! 🚀',
        message: 'Your admin notification system is working perfectly.',
        url: '/tuition'
      });
      
      // Success feedback: quick flash
      const originalColor = btn.className;
      btn.className = `${originalColor} ring-4 ring-green-400 scale-95 transition-all`;
      setTimeout(() => {
        btn.className = originalColor;
      }, 500);
    } catch (error) {
      console.error('Failed to send admin test notification:', error);
    } finally {
      setIsAdminTestingNotification(false);
    }
  };

  // Load tutees from Supabase
  useEffect(() => {
    const loadTutees = async () => {
      try {
        setLoadingTutees(true);
        const loadedTutees = await getTutees();
        setTutees(loadedTutees);
      } catch (error) {
        console.error('Failed to load tutees:', error);
      } finally {
        setLoadingTutees(false);
      }
    };
    loadTutees();
  }, []);

  useEffect(() => {
    if (tutees.length === 0) return;
    if (!selectedStudentsTuteeId || !tutees.some(t => t.id === selectedStudentsTuteeId)) {
      setSelectedStudentsTuteeId(tutees[0].id);
    }
    if (!selectedLearningTuteeId || !tutees.some(t => t.id === selectedLearningTuteeId)) {
      setSelectedLearningTuteeId(tutees[0].id);
    }
  }, [tutees, selectedStudentsTuteeId, selectedLearningTuteeId]);

  // Listen for PIN entered event (tutee PIN)
  useEffect(() => {
    const handlePinEntered = async (e: CustomEvent<{ pin: string }>) => {
      if (pendingTutee) {
        const isValid = await verifyPin(pendingTutee.id, e.detail.pin);
        
        // Dispatch result back to PinProtection component
        const resultEvent = new CustomEvent('pinResult', { 
          detail: { verified: isValid } 
        });
        window.dispatchEvent(resultEvent);

        if (isValid) {
          // Reload tutee to get latest colors
          const updatedTutee = tutees.find(t => t.id === pendingTutee.id) || pendingTutee;
          setSelectedTutee(updatedTutee);
          setShowPinModal(false);
          setPendingTutee(null);
        }
      }
    };

    window.addEventListener('pinEntered' as any, handlePinEntered as unknown as EventListener);
    return () => {
      window.removeEventListener('pinEntered' as any, handlePinEntered as unknown as EventListener);
    };
  }, [pendingTutee, tutees]);

  // Listen for admin PIN entered event
  useEffect(() => {
    const handleAdminPinEntered = async (e: CustomEvent<{ pin: string }>) => {
      try {
        const { data, error } = await supabase.functions.invoke('verify-admin', {
          body: { pin: e.detail.pin }
        });

        const isValid = !error && data?.verified;
        
        // Dispatch result back to AdminPinProtection component
        const resultEvent = new CustomEvent('adminPinResult', { 
          detail: { verified: isValid } 
        });
        window.dispatchEvent(resultEvent);

        if (isValid) {
          setIsAdmin(true);
          setShowAdminPinModal(false);
        }
      } catch (err) {
        console.error('Failed to verify admin PIN:', err);
        const resultEvent = new CustomEvent('adminPinResult', { 
          detail: { verified: false } 
        });
        window.dispatchEvent(resultEvent);
      }
    };

    window.addEventListener('adminPinEntered' as any, handleAdminPinEntered as any);
    return () => {
      window.removeEventListener('adminPinEntered' as any, handleAdminPinEntered as any);
    };
  }, []);

  const handleTuteeClick = (tutee: Tutee) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (mobileActionsTuteeId === tutee.id) {
      setMobileActionsTuteeId(null);
      return;
    }
    setPendingTutee(tutee);
    setShowPinModal(true);
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTuteeCardTouchStart = (tuteeId: string) => () => {
    if (!isAdmin) return;
    longPressTriggeredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setMobileActionsTuteeId(tuteeId);
      navigator.vibrate?.(10);
    }, 500);
  };

  const handleTuteeCardTouchEnd = () => {
    clearLongPressTimer();
  };

  const handlePinCancel = () => {
    setShowPinModal(false);
    setPendingTutee(null);
  };

  const handlePinVerified = () => {
    // This is called after PIN is verified
    // The state is already updated in the event handler
  };

  const handleBackToPortal = async () => {
    setSelectedTutee(null);
    // Reload tutees to get updated icons/names/descriptions
    try {
      const loadedTutees = await getTutees();
      setTutees(loadedTutees);
    } catch (error) {
      console.error('Failed to reload tutees:', error);
    }
  };

  const handleAdminClick = () => {
    setShowAdminPinModal(true);
  };

  const handleAdminPinCancel = () => {
    setShowAdminPinModal(false);
  };

  const handleAdminPinVerified = () => {
    // State is already updated in the event handler
  };

  const handleExitAdmin = () => {
    setIsAdmin(false);
  };

  // Update document title
  useDocumentTitle(selectedTutee ? `Tuition - ${selectedTutee.name}` : 'Tuition Portal');

  // Force light theme for tuition pages
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    return () => {
      // Don't restore theme on unmount - let ThemeContext handle it
    };
  }, []);

  // Show tutee dashboard if one is selected
  if (selectedTutee) {
    return <TuteeDashboard tutee={selectedTutee} onBack={handleBackToPortal} />;
  }

  // Get icon component
  const getIcon = (iconName: string) => {
    return getTuteeIcon(iconName, GraduationCap);
  };

  return (
    <div className="min-h-screen relative bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_30%),radial-gradient(circle_at_85%_20%,_rgba(236,72,153,0.14),_transparent_26%),linear-gradient(135deg,_#f5f7ff_0%,_#faf5ff_50%,_#fff7fb_100%)] pb-20 sm:pb-8">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-16 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-indigo-300/20 blur-3xl" />
        <div className="absolute top-32 right-[12%] h-64 w-64 rounded-full bg-fuchsia-200/20 blur-3xl" />
        <div className="absolute bottom-20 left-[10%] h-64 w-64 rounded-full bg-sky-200/20 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        <div className="relative rounded-[2rem] sm:rounded-[2.5rem] border border-white/60 bg-white/55 backdrop-blur-2xl shadow-[0_25px_80px_-35px_rgba(79,70,229,0.35)]">
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-r from-indigo-500/8 via-purple-500/10 to-pink-500/8" />

          {/* Header */}
          <div className="relative text-center px-5 sm:px-8 lg:px-12 pt-8 sm:pt-12 pb-6 sm:pb-8">
            {!isAdmin && (
              <button
                onClick={handleAdminClick}
                aria-label="Admin access"
                title="Admin access"
                className="absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex items-center justify-center h-9 w-9 rounded-full text-gray-400/70 hover:text-gray-500 hover:bg-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400/40 transition-all duration-300"
              >
                <Shield className="w-[18px] h-[18px]" />
              </button>
            )}

            <div className="mb-5 sm:mb-7">
              <TuitionMascot />
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 mb-3 px-2">
              Tuition Portal
            </h1>
            <p className="text-gray-500 text-sm sm:text-base px-2 max-w-xl mx-auto leading-relaxed">
              Select your profile to get started.
            </p>
          </div>

          {/* Tutee Selection Grid */}
          <div className="px-5 sm:px-8 lg:px-12 pb-8 sm:pb-10">
            {loadingTutees ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex min-h-[4.75rem] items-center gap-3.5 rounded-[1.5rem] border border-white/80 bg-white/80 shadow-[0_16px_45px_-28px_rgba(15,23,42,0.35)] px-4 py-3.5 sm:min-h-[5.25rem] sm:gap-4 sm:px-5 sm:py-4">
                    <Skeleton className="h-12 w-12 shrink-0 rounded-2xl sm:h-14 sm:w-14" />
                    <Skeleton className="h-5 w-2/3" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8 items-stretch">
                {tutees.map((tutee, index) => {
                  const IconComponent = getIcon(tutee.icon);
                  const gradientClass = `bg-gradient-to-br ${tutee.colorScheme.gradient}`;
                  const colorClasses = getColorClasses(tutee.colorScheme.primary);
                  const tintHex = getPrimaryHex(tutee.colorScheme.primary);
                  const actionsRevealed = mobileActionsTuteeId === tutee.id;

                  return (
                    <div
                      key={tutee.id}
                      className="group/tutee-card relative h-full animate-fade-in-up"
                      style={{ animationDelay: `${index * 90}ms` }}
                      onTouchStart={handleTuteeCardTouchStart(tutee.id)}
                      onTouchEnd={handleTuteeCardTouchEnd}
                      onTouchMove={handleTuteeCardTouchEnd}
                      onContextMenu={isAdmin ? (e) => e.preventDefault() : undefined}
                    >
                      {isAdmin && (
                        <div
                          data-tutee-actions={tutee.id}
                          onClick={(e) => e.stopPropagation()}
                          className={`absolute top-2 right-2 z-10 flex gap-1 items-center bg-white/92 backdrop-blur-md p-1 rounded-xl shadow-sm transition-opacity duration-200 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:pointer-events-none [@media(hover:hover)_and_(pointer:fine)]:group-hover/tutee-card:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:group-hover/tutee-card:pointer-events-auto [@media(hover:none)]:opacity-0 [@media(hover:none)]:pointer-events-none${actionsRevealed ? ' [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto' : ''}`}
                        >
                          <TuteeDeleteModal tutee={tutee} onDeleted={(id) => setTutees(prev => prev.filter(t => t.id !== id))} />
                          <TuteeEditor tutee={tutee} onUpdate={(updated) => setTutees(prev => prev.map(t => t.id === updated.id ? updated : t))} />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleTuteeClick(tutee)}
                        aria-label={`Open ${tutee.name}`}
                        className={`group relative flex h-full min-h-[4.75rem] w-full items-center gap-3.5 overflow-hidden rounded-[1.5rem] border border-white/85 bg-white/82 px-4 py-3.5 text-left shadow-[0_16px_45px_-28px_rgba(15,23,42,0.34)] backdrop-blur-md transition-all duration-300 touch-manipulation hover:-translate-y-0.5 hover:shadow-[0_24px_55px_-28px_rgba(15,23,42,0.4)] hover:ring-2 focus-visible:outline-none focus-visible:ring-2 sm:min-h-[5.25rem] sm:gap-4 sm:px-5 sm:py-4 ${colorClasses.ring} ring-offset-2 ring-offset-white/60${actionsRevealed ? ' ring-2' : ''}`}
                      >
                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-0 rounded-[1.5rem]"
                          style={{ backgroundColor: tintHex, opacity: 0.03 }}
                        />
                        <div className="relative flex-shrink-0">
                          <div className={`absolute inset-0 ${gradientClass} rounded-[1.15rem] blur-xl opacity-30 group-hover:opacity-45 transition-opacity`} />
                          <div className={`relative p-2.5 sm:p-3 ${gradientClass} rounded-[1.15rem] shadow-md group-hover:scale-105 transition-transform duration-300 group-active:scale-95`}>
                            <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                          </div>
                        </div>
                        <div className="relative min-w-0 flex-1">
                          <h2 className="truncate text-lg sm:text-xl font-bold tracking-tight text-gray-900">
                            {tutee.name}
                          </h2>
                        </div>
                        <ChevronRight
                          aria-hidden
                          className="relative w-5 h-5 flex-shrink-0 text-gray-300 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-gray-400"
                        />
                      </button>
                    </div>
                  );
                })}
                {isAdmin && (
                  <TuteeCreator 
                    onCreated={(newTutee) => setTutees(prev => [...prev, newTutee])}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Admin Section (only in admin mode) */}
        {isAdmin && (
          <div className="mt-6 space-y-6 animate-fade-in">
            <div className="bg-gradient-to-br from-[#000000] via-[#0d0d0d] to-[#1a1a1a] text-white rounded-xl sm:rounded-2xl p-3 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 border border-white/10 shadow-lg shadow-black/25">
              <div className="flex items-center gap-2.5 sm:gap-4 text-center sm:text-left w-full sm:w-auto justify-center sm:justify-start">
                <div className="p-2 sm:p-3 bg-white/10 rounded-xl sm:rounded-2xl backdrop-blur-md flex-shrink-0 ring-1 ring-white/10">
                  <Shield className="w-5 h-5 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <h3 className="font-bold text-base sm:text-xl lg:text-2xl">Admin Mode Active</h3>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                {notificationService.isSupported() && (
                  <button
                    onClick={handleAdminNotificationToggle}
                    onContextMenu={handleAdminTestNotification}
                    onTouchStart={(e) => {
                      const timer = setTimeout(() => handleAdminTestNotification(e as any), 800);
                      (e.currentTarget as any)._longPressTimer = timer;
                    }}
                    onTouchEnd={(e) => {
                      clearTimeout((e.currentTarget as any)._longPressTimer);
                    }}
                    disabled={isCheckingAdminSub || isTogglingAdminSub || isAdminTestingNotification}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-lg flex-1 sm:flex-none justify-center relative overflow-hidden min-h-[44px] touch-manipulation ${
                      isAdminSubscribed 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
                    }`}
                    title={isAdminSubscribed ? 'Right-click to test / Click to disable' : 'Enable Admin Notifs'}
                  >
                    {/* Background Pulse during testing */}
                    {isAdminTestingNotification && (
                      <div className="absolute inset-0 bg-green-200/50 animate-pulse" />
                    )}

                    <div className="relative flex items-center gap-1.5 sm:gap-2">
                      {isTogglingAdminSub || isAdminTestingNotification ? (
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      ) : isAdminSubscribed ? (
                        <Bell className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                      ) : (
                        <BellOff className="w-4 h-4 sm:w-5 sm:h-5" />
                      )}
                      <span className="whitespace-nowrap">
                        {isAdminTestingNotification 
                          ? 'Testing...' 
                          : isAdminSubscribed 
                            ? 'Notifs On' 
                            : 'Notifs Off'}
                      </span>
                    </div>
                  </button>
                )}
                <button
                  onClick={handleExitAdmin}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2.5 sm:py-3 bg-white text-gray-900 font-bold text-xs sm:text-sm rounded-xl hover:bg-gray-100 transition-all shadow-lg press-effect flex-1 sm:flex-none justify-center min-h-[44px] touch-manipulation whitespace-nowrap"
                >
                  <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Exit</span>
                </button>
              </div>
            </div>

            <div className="rounded-xl sm:rounded-2xl border border-gray-200/80 bg-white/95 shadow-sm shadow-black/5">
              <div
                role="tablist"
                aria-label="Admin sections"
                className="flex w-full max-sm:justify-between sm:grid sm:grid-cols-5 gap-1 sm:gap-2 p-2 sm:p-3 max-sm:overflow-x-auto custom-scrollbar max-sm:snap-x max-sm:snap-mandatory [-webkit-overflow-scrolling:touch] border-b border-gray-200/70 bg-gradient-to-b from-white to-gray-50/90"
              >
                {(
                  [
                    { id: 'schedule' as const, label: 'Schedule', Icon: CalendarDays },
                    { id: 'students' as const, label: 'Students', Icon: Users },
                    { id: 'learning' as const, label: 'Learning', Icon: BookOpen },
                    { id: 'business' as const, label: 'Business', Icon: CircleDollarSign },
                    { id: 'system' as const, label: 'System', Icon: Settings2 },
                  ] as const
                ).map(({ id, label, Icon }) => {
                  const selected = adminTab === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      aria-controls={`admin-panel-${id}`}
                      id={`admin-tab-${id}`}
                      aria-label={label}
                      title={label}
                      onClick={() => setAdminTab(id)}
                      className={`flex-shrink-0 max-sm:snap-start sm:w-full inline-flex items-center justify-center gap-2 max-sm:min-h-[44px] max-sm:min-w-[44px] max-sm:px-0 max-sm:py-0 sm:px-4 sm:py-2.5 rounded-xl text-sm font-bold transition-all min-h-[44px] touch-manipulation ring-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/30 ${
                        selected
                          ? 'bg-gradient-to-br from-[#000000] to-[#1a1a1a] text-white shadow-md shadow-black/20 ring-1 ring-white/10'
                          : 'bg-white/95 text-gray-600 hover:bg-white hover:text-gray-900 border border-gray-200/80 hover:border-gray-300 sm:border-transparent sm:hover:border-gray-200'
                      }`}
                    >
                      <Icon
                        className="w-[1.125rem] h-[1.125rem] sm:w-[18px] sm:h-[18px] flex-shrink-0 opacity-95"
                        aria-hidden
                      />
                      <span className="whitespace-nowrap hidden sm:inline">{label}</span>
                    </button>
                  );
                })}
              </div>

              {((adminTab === 'students' && selectedStudentsTuteeId) ||
                (adminTab === 'learning' && selectedLearningTuteeId)) && (
                <div className="px-2 sm:px-4">
                  <StickyAdminGroupBar
                    tutees={tutees}
                    selectedTuteeId={
                      adminTab === 'students' ? selectedStudentsTuteeId : selectedLearningTuteeId
                    }
                    onSelect={
                      adminTab === 'students' ? setSelectedStudentsTuteeId : setSelectedLearningTuteeId
                    }
                  />
                </div>
              )}

              <div className="p-2 sm:p-4 bg-white/40">
                <div
                  id="admin-panel-schedule"
                  role="tabpanel"
                  aria-labelledby="admin-tab-schedule"
                  hidden={adminTab !== 'schedule'}
                  className={
                    adminTab === 'schedule'
                      ? 'grid grid-cols-1 gap-4 sm:gap-6'
                      : 'hidden'
                  }
                >
                  <AdminTuitionCalendar />
                  <BookingRequestsAdmin />
                </div>
                <div
                  id="admin-panel-students"
                  role="tabpanel"
                  aria-labelledby="admin-tab-students"
                  hidden={adminTab !== 'students'}
                  className={
                    adminTab === 'students'
                      ? 'grid grid-cols-1 gap-4 sm:gap-6'
                      : 'hidden'
                  }
                >
                  <AdminCollapsibleSection
                    id="module-manager"
                    title="Module Manager"
                    subtitle="Assign reusable modules to tuition groups"
                    icon={<div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-600"><Layout className="w-5 h-5" /></div>}
                  >
                    <ComponentManager tutees={tutees} selectedTuteeId={selectedStudentsTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="global-files"
                    title="Global Resource Manager"
                    subtitle="Files and links shared across groups"
                    icon={<div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-600"><HardDrive className="w-5 h-5" /></div>}
                  >
                    <GlobalFileManager tutees={tutees} selectedTuteeId={selectedStudentsTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="messaging"
                    title="Messaging Center"
                    subtitle="Direct chat with students"
                    icon={<div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-600"><MessageCircle className="w-5 h-5" /></div>}
                  >
                    <MessagingAdmin tutees={tutees} selectedTuteeId={selectedStudentsTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="student-management"
                    title="Student Management"
                    subtitle="Manage students for each tutee group"
                    icon={<div className="p-2.5 bg-purple-100 rounded-xl text-purple-600"><Users className="w-5 h-5" /></div>}
                  >
                    <StudentManagement tutees={tutees} selectedTuteeId={selectedStudentsTuteeId} />
                  </AdminCollapsibleSection>
                </div>
                <div
                  id="admin-panel-learning"
                  role="tabpanel"
                  aria-labelledby="admin-tab-learning"
                  hidden={adminTab !== 'learning'}
                  className={
                    adminTab === 'learning'
                      ? 'grid grid-cols-1 gap-4 sm:gap-6'
                      : 'hidden'
                  }
                >
                  <AdminCollapsibleSection
                    id="spelling-quiz"
                    title="Spelling Quiz"
                    subtitle="Words, questions, and quiz settings"
                    icon={<div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-600"><BookOpen className="w-5 h-5" /></div>}
                  >
                    <SpellingQuizConfig tutees={tutees} selectedTuteeId={selectedLearningTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="hanzi-hero"
                    title="Hanzi Hero"
                    subtitle="Characters, practice stats, and prize settings"
                    icon={<div className="p-2.5 bg-amber-100 rounded-xl text-amber-600"><PenTool className="w-5 h-5" /></div>}
                  >
                    <HanziHeroConfig tutees={tutees} selectedTuteeId={selectedLearningTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="flashcards"
                    title="Flashcards"
                    subtitle="Decks, cards, and SM-2 settings"
                    icon={<div className="p-2.5 bg-violet-100 rounded-xl text-violet-600"><Layers className="w-5 h-5" /></div>}
                  >
                    <FlashcardsConfig tutees={tutees} selectedTuteeId={selectedLearningTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="learning-points"
                    title="AI Review Insights"
                    subtitle="Learning point review history"
                    icon={<div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-600"><Brain className="w-5 h-5" /></div>}
                  >
                    <LearningPointsAdmin tutees={tutees} selectedTuteeId={selectedLearningTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="gpt-chat"
                    title="AI Chat Insights"
                    subtitle="Student interactions with the AI assistant"
                    icon={<div className="p-2.5 bg-indigo-100 rounded-xl text-indigo-600"><Bot className="w-5 h-5" /></div>}
                  >
                    <GPTChatAdmin tutees={tutees} selectedTuteeId={selectedLearningTuteeId} />
                  </AdminCollapsibleSection>
                  <AdminCollapsibleSection
                    id="progress-reports"
                    title="AI Progress Reports"
                    subtitle="Generate and send parent updates"
                    icon={<div className="p-2.5 bg-purple-100 rounded-xl text-purple-600"><Sparkles className="w-5 h-5" /></div>}
                  >
                    <ProgressReportAdmin tutees={tutees} selectedTuteeId={selectedLearningTuteeId} />
                  </AdminCollapsibleSection>
                </div>
                <div
                  id="admin-panel-business"
                  role="tabpanel"
                  aria-labelledby="admin-tab-business"
                  hidden={adminTab !== 'business'}
                  className={
                    adminTab === 'business'
                      ? 'grid grid-cols-1 gap-4 sm:gap-6'
                      : 'hidden'
                  }
                >
                  <FeedbackAdmin />
                  <EarningsAdmin
                    tutees={tutees}
                    initialTuteeId={selectedEarningsTuteeId}
                    onTuteeSelectChange={setSelectedEarningsTuteeId}
                  />
                </div>
                <div
                  id="admin-panel-system"
                  role="tabpanel"
                  aria-labelledby="admin-tab-system"
                  hidden={adminTab !== 'system'}
                  className={
                    adminTab === 'system'
                      ? 'grid grid-cols-1 gap-4 sm:gap-6'
                      : 'hidden'
                  }
                >
                  <NotificationAdmin />
                  <LandingPageSettings />
                  <AdminQuickLinks />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>


      {/* PIN Protection Modal */}
      {showPinModal && pendingTutee && (
        <PinProtection
          tuteeName={pendingTutee.name}
          onPinVerified={handlePinVerified}
          onCancel={handlePinCancel}
        />
      )}

      {/* Admin PIN Protection Modal */}
      {showAdminPinModal && (
        <AdminPinProtection
          onPinVerified={handleAdminPinVerified}
          onCancel={handleAdminPinCancel}
          pinLength={ADMIN_CONFIG.pinLength}
        />
      )}
    </div>
  );
};

export default Tuition;
